import { ChevronsUpDown, ClipboardX } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';

interface TranscriptMessage {
  role: string;
  content: string;
  copyPasteDetected?: boolean;
  createdAt: string;
}

interface TranscriptViewProps {
  messages: TranscriptMessage[];
}

export function TranscriptView({ messages }: TranscriptViewProps): React.ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (messages.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('scoring.transcript')}</h2>
        <CollapsibleTrigger className="inline-flex h-8 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
          <ChevronsUpDown className="size-4" />
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          {messages.map((msg) => {
            const isFlagged = msg.role !== 'assistant' && msg.copyPasteDetected;
            return (
              <div
                key={msg.createdAt}
                className={`flex flex-col ${msg.role === 'assistant' ? 'items-start' : 'items-end'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'assistant'
                      ? 'bg-secondary text-secondary-foreground'
                      : isFlagged
                        ? 'bg-primary text-primary-foreground ring-2 ring-amber-400 ring-offset-1'
                        : 'bg-primary text-primary-foreground'
                  }`}
                >
                  {msg.content}
                </div>
                {isFlagged && (
                  <div className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-600">
                    <ClipboardX className="size-3" />
                    <span>{t('scoring.copyPasteDetected')}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
